import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { VelocityDirection } from 'types/sphereMetrics';
import './VelocitySparklineAtom.scss';

export interface VelocityDataPoint {
  /** Velocity score from -100 to +100 */
  score: number;
  /** Optional ISO timestamp for the data point */
  timestamp?: string;
}

export interface VelocitySparklineAtomProps {
  /** Array of velocity data points ordered chronologically (oldest first) */
  dataPoints: VelocityDataPoint[];
  /** Overall trend direction, determines line color */
  trend?: VelocityDirection;
  /** Height of the sparkline in pixels */
  height?: number;
  /** Additional CSS class */
  className?: string;
  /** Render a filled gradient area under the line (subscriber growth usage) */
  areaFill?: boolean;
  /** Custom hue (0-359) for the line and fill color when areaFill is true */
  hue?: number;
  /** Value range the scores map into; defaults to the velocity score range [-100, 100] */
  domain?: [number, number];
  /** Render a dot at the last data point (numeric-aspect track usage) */
  endpointDot?: boolean;
  /** Click handler for the endpoint dot; when set, the dot becomes focusable and interactive */
  onEndpointClick?: () => void;
  /** Overrides the default translated aria-label */
  ariaLabel?: string;
}

const DEFAULT_DOMAIN: [number, number] = [-100, 100];

/** Maps the trend direction to the CSS modifier class */
function getTrendClass(trend: VelocityDirection): string {
  return `velocity-sparkline-atom--${trend}`;
}

/**
 * Converts data points to an SVG polyline points string.
 * X is evenly distributed across width, Y maps score (-100..+100) to height.
 */
function normalizeScore(score: number, domain: [number, number]): number {
  const [min, max] = domain;
  return (score - min) / (max - min);
}

function buildPolylinePoints(
  dataPoints: VelocityDataPoint[],
  width: number,
  height: number,
  padding: number,
  domain: [number, number]
): string {
  if (dataPoints.length === 0) return '';

  const drawHeight = height - padding * 2;
  const drawWidth = width - padding * 2;
  const count = dataPoints.length;
  const xStep = count > 1 ? drawWidth / (count - 1) : 0;

  return dataPoints
    .map((point, i) => {
      const x = padding + i * xStep;
      // Map score from domain to [drawHeight + padding, padding] (inverted Y)
      const normalized = normalizeScore(point.score, domain);
      const y = padding + drawHeight * (1 - normalized);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/** Returns the pixel coordinates of the last data point (used for the endpoint dot). */
function buildEndpoint(
  dataPoints: VelocityDataPoint[],
  width: number,
  height: number,
  padding: number,
  domain: [number, number]
): { x: number; y: number } | null {
  if (dataPoints.length === 0) return null;

  const drawHeight = height - padding * 2;
  const drawWidth = width - padding * 2;
  const count = dataPoints.length;
  const xStep = count > 1 ? drawWidth / (count - 1) : 0;
  const lastIndex = count - 1;

  const x = padding + lastIndex * xStep;
  const normalized = normalizeScore(dataPoints[lastIndex].score, domain);
  const y = padding + drawHeight * (1 - normalized);
  return { x, y };
}

/**
 * VelocitySparklineAtom - Mini SVG line chart showing velocity trend over time.
 * Renders a simple polyline from velocity score data points with color-coded trend.
 */
/** Builds a closed SVG path for a filled area under the polyline. */
function buildAreaPath(
  dataPoints: VelocityDataPoint[],
  width: number,
  height: number,
  padding: number,
  domain: [number, number]
): string {
  if (dataPoints.length < 2) return '';
  const drawHeight = height - padding * 2;
  const drawWidth = width - padding * 2;
  const count = dataPoints.length;
  const xStep = count > 1 ? drawWidth / (count - 1) : 0;

  const pts = dataPoints.map((point, i) => {
    const x = padding + i * xStep;
    const normalized = normalizeScore(point.score, domain);
    const y = padding + drawHeight * (1 - normalized);
    return { x, y };
  });

  const firstX = pts[0].x;
  const lastX = pts[pts.length - 1].x;
  const baselineY = padding + drawHeight;

  return [
    `M ${firstX.toFixed(1)},${baselineY.toFixed(1)}`,
    ...pts.map(({ x, y }) => `L ${x.toFixed(1)},${y.toFixed(1)}`),
    `L ${lastX.toFixed(1)},${baselineY.toFixed(1)}`,
    'Z',
  ].join(' ');
}

const VelocitySparklineAtom: React.FC<VelocitySparklineAtomProps> = ({
  dataPoints,
  trend = 'stable',
  height = 40,
  className = '',
  areaFill = false,
  hue,
  domain = DEFAULT_DOMAIN,
  endpointDot = false,
  onEndpointClick,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const viewBoxWidth = 200;
  const padding = 4;

  const points = useMemo(
    () => buildPolylinePoints(dataPoints, viewBoxWidth, height, padding, domain),
    [dataPoints, height, domain]
  );

  const areaPath = useMemo(
    () => (areaFill ? buildAreaPath(dataPoints, viewBoxWidth, height, padding, domain) : ''),
    [areaFill, dataPoints, height, domain]
  );

  const endpoint = useMemo(
    () => (endpointDot ? buildEndpoint(dataPoints, viewBoxWidth, height, padding, domain) : null),
    [endpointDot, dataPoints, height, domain]
  );

  const hueStyle = hue === undefined ? undefined : ({ '--sparkline-hue': hue } as React.CSSProperties);

  const classes = [
    'velocity-sparkline-atom',
    getTrendClass(trend),
    areaFill ? 'velocity-sparkline-atom--area' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (dataPoints.length < 2) {
    return (
      <div
        className={`velocity-sparkline-atom velocity-sparkline-atom--empty ${className}`}
        style={{ height }}
      />
    );
  }

  return (
    <svg
      className={classes}
      viewBox={`0 0 ${viewBoxWidth} ${height}`}
      preserveAspectRatio="none"
      style={{ height, ...hueStyle }}
      // An `img` must not contain focusable descendants (axe `nested-interactive`), and with a
      // clickable endpoint this is a labelled group holding one control rather than a picture.
      role={onEndpointClick ? 'group' : 'img'}
      aria-label={ariaLabel ?? t('statusPanel.sparkline.ariaLabel')}
    >
      {areaFill && areaPath && (
        <path
          className="velocity-sparkline-atom__area"
          d={areaPath}
          strokeWidth="0"
        />
      )}
      <polyline
        className="velocity-sparkline-atom__line"
        points={points}
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {endpoint && (
        <circle
          className="velocity-sparkline-atom__endpoint"
          cx={endpoint.x}
          cy={endpoint.y}
          r="4.5"
          onClick={onEndpointClick}
          role={onEndpointClick ? 'button' : undefined}
          // A `button` needs its own name; the group's label describes the chart, not this act.
          aria-label={onEndpointClick ? t('statusPanel.sparkline.endpointAriaLabel') : undefined}
          tabIndex={onEndpointClick ? 0 : undefined}
          onKeyDown={
            onEndpointClick
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onEndpointClick();
                  }
                }
              : undefined
          }
        />
      )}
    </svg>
  );
};

VelocitySparklineAtom.displayName = 'VelocitySparklineAtom';

export default VelocitySparklineAtom;
